import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Divider } from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { useAuth } from "../context/AuthContext";
import AuthPanel from "../components/AuthPanel";
import MeApi from "../api/MeApi";

export default function MyData() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [consents, setConsents] = useState<{ type: string; granted: boolean }[]>([]);
  useEffect(() => { if (user) MeApi.consents().then(setConsents).catch(() => {}); }, [user]);
  const toggleConsent = async (type: string, granted: boolean) => {
    await MeApi.setConsent(type, granted ? "withdraw" : "grant");
    setConsents(await MeApi.consents());
  };

  if (loading) return null;
  if (!user) {
    return (
      <Container maxWidth="xs" sx={{ py: { xs: 6, sm: 10 } }}>
        <Typography sx={{ mb: 2 }}>{t("dsar.loginPrompt")}</Typography>
        <AuthPanel initialMode="login" onSuccess={() => {}} />
      </Container>
    );
  }

  const download = async () => {
    const data = await MeApi.exportData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "carefunded-mijn-gegevens.json";
    a.click();
    URL.revokeObjectURL(url);
  };
  const doDelete = async () => {
    setBusy(true);
    try { await MeApi.deleteAccount(); await logout(); navigate("/"); }
    finally { setBusy(false); setConfirmOpen(false); }
  };

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "2.25rem", sm: "2.75rem" }, mb: 1.5 }}>{t("dsar.title")}</Typography>
        <Typography sx={{ color: "text.secondary", mb: 4, maxWidth: 620 }}>{t("dsar.intro")}</Typography>

        <Box sx={{ p: { xs: 3, sm: 4 }, mb: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
          <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: 1 }}>{t("dsar.exportTitle")}</Typography>
          <Typography sx={{ color: "text.secondary", mb: 2.5 }}>{t("dsar.exportBody")}</Typography>
          <Button variant="contained" startIcon={<DownloadRoundedIcon />} onClick={download}>{t("dsar.exportBtn")}</Button>
        </Box>

        {consents.length > 0 && (
          <Box sx={{ p: { xs: 3, sm: 4 }, mb: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
            <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: 1 }}>{t("dsar.consentsTitle")}</Typography>
            <Typography sx={{ color: "text.secondary", mb: 2 }}>{t("dsar.consentsBody")}</Typography>
            <Stack divider={<Divider />} spacing={0}>
              {consents.map((c) => (
                <Box key={c.type} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>{t(`dsar.consent.${c.type}`)}</Typography>
                    <Typography sx={{ fontSize: ".85rem", color: "text.secondary" }}>{c.granted ? t("dsar.granted") : t("dsar.withdrawn")}</Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => toggleConsent(c.type, c.granted)}>{c.granted ? t("dsar.withdraw") : t("dsar.grant")}</Button>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
          <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: 1 }}>{t("dsar.deleteTitle")}</Typography>
          <Typography sx={{ color: "text.secondary", mb: 2.5 }}>{t("dsar.deleteBody")}</Typography>
          <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>{t("dsar.deleteBtn")}</Button>
        </Box>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("dsar.deleteConfirmTitle")}</DialogTitle>
        <DialogContent><Alert severity="warning">{t("dsar.deleteConfirmBody")}</Alert></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t("dsar.cancel")}</Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={busy}>{t("dsar.deleteConfirm")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
